/**
 * sl-728o.7.1: Tutorial Card Event Listener Hook
 *
 * Listens for 'tutorial-card-start' custom events from TutorialCard GfxBlocks
 * and delegates to TutorialLauncher. Also sets up the RBAC role checker
 * from workspace permission context.
 */

import { useServiceOptional } from '@toeverything/infra';
import { useEffect } from 'react';

import { WorkspacePermissionService } from '../../../permissions';
import { TutorialLauncher } from '../services/tutorial-launcher';

export function useTutorialCardListener(): void {
  const launcher = useServiceOptional(TutorialLauncher);
  const permissionService = useServiceOptional(WorkspacePermissionService);

  // Set up role checker when permission context is available
  useEffect(() => {
    if (!launcher || !permissionService) return;

    const perm = permissionService.permission;

    launcher.setRoleChecker((requiredRole: string) => {
      const role = requiredRole.toLowerCase();
      if (role === 'owner') {
        return perm.isOwner$.value ?? false;
      }
      if (role === 'admin') {
        return perm.isOwnerOrAdmin$.value ?? false;
      }
      // No specific role required — allow
      return true;
    });
  }, [launcher, permissionService]);

  // Listen for custom events from GfxBlock
  useEffect(() => {
    if (!launcher) return;

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.sessionId) {
        launcher.handleStart(detail);
      }
    };

    window.addEventListener('tutorial-card-start', handler);
    return () => window.removeEventListener('tutorial-card-start', handler);
  }, [launcher]);
}
